import usePostBulkUploadImages, { ImageType } from '../../../services/queries/usePostBulkUploadImages'
import { setupTokenRefresh, refreshAuthToken } from './tokenRefresh'

const BULK_IMAGE_BATCH_SIZE = 10
const PARALLEL_IMAGE_BATCHES = 4

interface ImageUrlInfo {
  url: string
  rowIndex: number
  type: string
}

const getImageTypeDisplayName = (imageType: ImageType): string => {
  const displayNames: Record<ImageType, string> = {
    BRAND_LOGO: 'Brand Logo',
    BRAND_COVER: 'Brand Cover',
    AD_IMAGE: 'Ad',
    OFFER_IMAGE: 'Offer',
    BRAND_STORY_IMAGE: 'Brand Story',
    WRAPPER_IMAGE: 'Wrapper',
    GOOGLE_WALLET_LOGO: 'Google Wallet Logo',
    APPLE_WALLET_LOGO: 'Apple Wallet Logo',
    GOOGLE_WALLET_IMAGE: 'Google Wallet',
    APPLE_WALLET_IMAGE: 'Apple Wallet',
  }
  return displayNames[imageType] || imageType
}

/**
 * Fetches an image from a URL and converts it to a File object
 */
export const fetchImageAsBlob = async (url: string): Promise<File> => {
  try {
    const response = await fetch(url)
    if (!response.ok) throw new Error('Failed to fetch image')
    const blob = await response.blob()
    const fileName = url.split('/').pop() || 'image.jpg'
    return new File([blob], fileName, { type: blob.type })
  } catch (err) {
    throw new Error(`Failed to fetch image from ${url}: ${err}`)
  }
}

/**
 * Checks if a URL is already uploaded (matches the format of uploaded images)
 * URLs that start with '/' or contain 'brand/' are considered already uploaded
 * Format: brand/68e3abdca50c24e97a91e33c/apple_wallet_image/3da8
 */
const isAlreadyUploaded = (url: string): boolean => {
  if (!url) return false
  // Check if URL starts with '/' (relative path) or contains 'brand/' (uploaded format)
  return url.startsWith('/') || url.includes('brand/')
}

/**
 * Groups image URLs by type from CSV data
 * Skips logo uploads if they match the uploaded format (contain '/brand' or start with '/')
 */
export const groupImageUrlsByType = (
  csvData: Array<{
    AppleWalletImageUrl?: string
    GoogleWalletImageUrl?: string
    AppleWalletLogoUrl?: string
    GoogleWalletLogoUrl?: string
    appleWalletImageUrl?: string
    googleWalletImageUrl?: string
    appleWalletLogoUrl?: string
    googleWalletLogoUrl?: string
  }>
): Map<ImageType, ImageUrlInfo[]> => {
  const imageUrlsByType = new Map<ImageType, ImageUrlInfo[]>()

  csvData.forEach((row, index) => {
    const appleImageUrl = row.AppleWalletImageUrl || row.appleWalletImageUrl || ''
    const googleImageUrl = row.GoogleWalletImageUrl || row.googleWalletImageUrl || ''
    const appleLogoUrl = row.AppleWalletLogoUrl || row.appleWalletLogoUrl || ''
    const googleLogoUrl = row.GoogleWalletLogoUrl || row.googleWalletLogoUrl || ''

    const addImageUrl = (url: string, imgType: ImageType, type: string, skipIfUploaded = false) => {
      if (url) {
        // Skip logo uploads if they're already uploaded (match the format)
        if (skipIfUploaded && isAlreadyUploaded(url)) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`Skipping ${type} upload - already uploaded (matches brand/ pattern): ${url}`)
          }
          return
        }
        if (!imageUrlsByType.has(imgType)) {
          imageUrlsByType.set(imgType, [])
        }
        const typeArray = imageUrlsByType.get(imgType)
        if (typeArray) {
          typeArray.push({ url, rowIndex: index, type })
        }
      }
    }

    addImageUrl(appleImageUrl, 'APPLE_WALLET_IMAGE', 'appleImage')
    addImageUrl(googleImageUrl, 'GOOGLE_WALLET_IMAGE', 'googleImage')
    // Skip logo uploads if they match the uploaded format
    addImageUrl(appleLogoUrl, 'APPLE_WALLET_LOGO', 'appleLogo', true)
    addImageUrl(googleLogoUrl, 'GOOGLE_WALLET_LOGO', 'googleLogo', true)
  })

  return imageUrlsByType
}

interface ProcessImageUploadsParams {
  imageUrlsByType: Map<ImageType, ImageUrlInfo[]>
  brandId: string
  bulkUploadImagesMutation: ReturnType<typeof usePostBulkUploadImages>
  onProgress: (progress: number) => void
  onStatusChange: (status: string) => void
}

/**
 * Processes all image uploads grouped by type
 */
export const processImageUploads = async ({
  imageUrlsByType,
  brandId,
  bulkUploadImagesMutation,
  onProgress,
  onStatusChange,
}: ProcessImageUploadsParams): Promise<Map<string, string>> => {
  const imageUploadCache = new Map<string, string>()

  // Calculate total images to upload (excluding skipped logos)
  const totalImages = Array.from(imageUrlsByType.values()).reduce((sum, arr) => sum + arr.length, 0)
  let uploadedCount = 0

  // Initialize progress to 0
  onProgress(0)

  // Set up token refresh to keep session alive during long uploads
  const cleanupTokenRefresh = setupTokenRefresh()

  // Log summary of images by type (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('=== IMAGE UPLOAD SUMMARY ===')
    imageUrlsByType.forEach((urls, type) => {
      console.log(`${type}: ${urls.length} images`)
    })
    console.log(`Total images to upload: ${totalImages}`)
    console.log('===========================')
  }

  // Upload images by type in batches
  const imageTypeEntries = Array.from(imageUrlsByType.entries())

  const processImageType = async (imageType: ImageType, imageUrls: ImageUrlInfo[]) => {
    const batches: ImageUrlInfo[][] = []

    // Create all batches first
    for (let i = 0; i < imageUrls.length; i += BULK_IMAGE_BATCH_SIZE) {
      batches.push(imageUrls.slice(i, i + BULK_IMAGE_BATCH_SIZE))
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`Processing ${imageType}: ${imageUrls.length} images in ${batches.length} batch(es)`)
    }

    const processBatch = async (batch: ImageUrlInfo[], batchIndex: number, totalBatches: number) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`Batch ${batchIndex + 1}/${totalBatches} for ${imageType}: ${batch.length} images`)
      }

      // Fetch all images in batch as Files
      const imageFiles = await Promise.all(
        batch.map(async imgInfo => ({
          file: await fetchImageAsBlob(imgInfo.url),
          rowIndex: imgInfo.rowIndex,
          type: imgInfo.type,
        }))
      )

      // Upload batch using bulk endpoint
      const filesToUpload = imageFiles.map(item => item.file)
      if (process.env.NODE_ENV === 'development') {
        console.log(`Uploading ${filesToUpload.length} files in one request for ${imageType}`)
      }

      try {
        // Refresh token proactively before each image upload batch to prevent expiration
        await refreshAuthToken()

        const response = await bulkUploadImagesMutation.mutateAsync({
          brandId,
          type: imageType,
          files: filesToUpload,
        })

        // Map successful uploads back to cache
        if (process.env.NODE_ENV === 'development') {
          console.log(`Processing ${response.successfulUploads.length} successful uploads for ${imageType}`)
        }
        response.successfulUploads.forEach((upload, idx) => {
          const imgInfo = batch[idx]
          if (imgInfo) {
            // Use path field which contains the format: brand/.../apple_wallet_image/...
            const uploadedPath = upload.path || upload.imagePathCompressedAsJpeg || upload.webpUrl || ''
            const cacheKey = `${imgInfo.rowIndex}-${imgInfo.type}`
            imageUploadCache.set(cacheKey, uploadedPath)
            if (process.env.NODE_ENV === 'development') {
              console.log(`✓ Cached uploaded image: ${cacheKey} -> ${uploadedPath}`)
            }
          }
        })

        if (response.failedUploads.length > 0 && process.env.NODE_ENV === 'development') {
          console.warn(`Some images failed to upload in batch:`, response.failedUploads)
        }

        return response.successCount
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`Failed to upload batch of ${imageType}:`, err)
        }
        return 0
      }
    }

    // Process batches in groups
    const batchGroups: ImageUrlInfo[][][] = []
    for (let i = 0; i < batches.length; i += PARALLEL_IMAGE_BATCHES) {
      batchGroups.push(batches.slice(i, i + PARALLEL_IMAGE_BATCHES))
    }

    const processGroupSequentially = async (
      previousTotal: Promise<number>,
      group: ImageUrlInfo[][],
      groupIndex: number
    ): Promise<number> => {
      const previousCount = await previousTotal
      const currentStart = groupIndex * PARALLEL_IMAGE_BATCHES

      const batchPromises = group.map((batch, batchGroupIndex) => {
        const batchIndex = currentStart + batchGroupIndex
        return processBatch(batch, batchIndex, batches.length)
      })

      const groupResults = await Promise.all(batchPromises)
      const groupSuccessCount = groupResults.reduce((sum, count) => sum + count, 0)
      const newTotal = previousCount + groupSuccessCount

      uploadedCount += groupSuccessCount
      onProgress(Math.round((uploadedCount / totalImages) * 100))

      return newTotal
    }

    await batchGroups.reduce(
      (previousPromise, group, groupIndex) => processGroupSequentially(previousPromise, group, groupIndex),
      Promise.resolve(0)
    )
  }

  // Process all image types sequentially
  const processAllTypesSequentially = async () => {
    let currentIndex = 0
    const processNext = async (): Promise<void> => {
      if (currentIndex >= imageTypeEntries.length) {
        // Ensure progress is 100% when done
        if (totalImages > 0) {
          onProgress(100)
        }
        return
      }
      const [imageType, imageUrls] = imageTypeEntries[currentIndex]
      currentIndex += 1

      // Skip if no images to upload for this type
      if (imageUrls.length === 0) {
        return processNext()
      }

      const typeDisplayName = getImageTypeDisplayName(imageType)
      onStatusChange(`Uploading ${typeDisplayName} images...`)

      await processImageType(imageType, imageUrls)
      return processNext()
    }
    return processNext()
  }

  try {
    await processAllTypesSequentially()

    // Final progress update
    if (totalImages > 0) {
      onProgress(100)
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('=== IMAGE UPLOAD COMPLETE ===')
      console.log(`Total images cached: ${imageUploadCache.size}`)
      console.log('============================')
    }
  } finally {
    // Always clean up token refresh interval, even if there's an error
    cleanupTokenRefresh()
  }

  return imageUploadCache
}
